'use client';

import React, { useState, useTransition, useId } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormState {
  status: 'idle' | 'success' | 'error';
  message: string;
}

interface FieldErrors {
  fullName?: string;
  email?: string;
  phone?: string;
  position?: string;
  emtCertified?: string;
  resume?: string;
  consent?: string;
}

const ALLOWED_POSITIONS = ['EMT', 'Medical Transport Driver', 'Other'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx'];
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email) && email.length <= 254;
}

function isValidPhone(phone: string): boolean {
  return /^[\d\s\-().+]{7,20}$/.test(phone.trim());
}

export default function CareersPageClient() {
  const formId = useId();
  const [isPending, startTransition] = useTransition();
  const [formState, setFormState] = useState<FormState>({ status: 'idle', message: '' });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const validate = (data: FormData): FieldErrors => {
    const errors: FieldErrors = {};
    const fullName = (data.get('fullName') as string || '').trim();
    const email = (data.get('email') as string || '').trim();
    const phone = (data.get('phone') as string || '').trim();
    const position = (data.get('position') as string || '').trim();
    const emtCertified = data.get('emtCertified') as string | null;
    const consent = data.get('consent');
    const resumeFile = data.get('resume') as File | null;

    if (!fullName || fullName.length < 2) errors.fullName = 'Please enter your full name.';
    if (!email) errors.email = 'Email address is required.';
    else if (!isValidEmail(email)) errors.email = 'Enter a valid email address.';
    if (!phone) errors.phone = 'Phone number is required.';
    else if (!isValidPhone(phone)) errors.phone = 'Enter a valid phone number (e.g. (201) 555-0100).';
    if (!position || !ALLOWED_POSITIONS.includes(position)) errors.position = 'Please select a position.';
    if (!emtCertified) errors.emtCertified = 'Please indicate your EMT certification status.';

    if (resumeFile && resumeFile.size > 0) {
      if (resumeFile.size > MAX_FILE_SIZE) {
        errors.resume = `File must be under ${MAX_FILE_SIZE / (1024 * 1024)} MB.`;
      } else if (!ALLOWED_MIME_TYPES.includes(resumeFile.type)) {
        errors.resume = 'Only PDF, DOC, or DOCX files are accepted.';
      } else {
        const ext = resumeFile.name.slice(resumeFile.name.lastIndexOf('.')).toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
          errors.resume = 'File must have a .pdf, .doc, or .docx extension.';
        }
      }
    }

    if (!consent) errors.consent = 'You must agree to be contacted to submit your application.';

    return errors;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const errors = validate(data);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    startTransition(async () => {
      try {
        const res = await fetch('/api/careers-application', {
          method: 'POST',
          body: data,
        });
        if (res.ok) {
          setFormState({
            status: 'success',
            message: "Application received! We'll be in touch within 2 business days to schedule a phone screen.",
          });
          form.reset();
        } else {
          const json = await res.json().catch(() => ({}));
          throw new Error(json.error || 'Server error');
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Something went wrong.';
        setFormState({
          status: 'error',
          message: msg === 'Server error' ?'Something went wrong. Please email your resume directly to careers@kapra-ems.com or call (551) 326-9573.'
            : msg,
        });
      }
    });
  };

  if (formState.status === 'success') {
    return (
      <div
        className="bg-sage/10 border border-sage/40 rounded-2xl p-8 text-center"
        role="alert"
        aria-live="polite"
      >
        <div className="w-14 h-14 rounded-full bg-sage/20 flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircleIcon" size={28} className="text-sage" variant="solid" />
        </div>
        <h3 className="font-display text-xl font-semibold text-primary mb-2">
          Application Received!
        </h3>
        <p className="text-muted-foreground leading-relaxed">{formState.message}</p>
        <button
          className="btn-outline-navy mt-6 text-sm"
          onClick={() => setFormState({ status: 'idle', message: '' })}
        >
          Submit Another Application
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      aria-label="Job application form"
      className="space-y-6"
      encType="multipart/form-data"
    >
      {formState.status === 'error' && (
        <div
          className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3"
          role="alert"
          aria-live="assertive"
        >
          <Icon name="ExclamationCircleIcon" size={18} className="text-red-500 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{formState.message}</p>
        </div>
      )}

      {/* Row 1: Name + Email */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`${formId}-name`} className="form-label-base">
            Full Name <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id={`${formId}-name`}
            name="fullName"
            type="text"
            required
            autoComplete="name"
            placeholder="Maria Rodriguez"
            className={`form-input-base${fieldErrors.fullName ? ' border-red-400 focus:ring-red-300' : ''}`}
            aria-required="true"
            aria-describedby={fieldErrors.fullName ? `${formId}-name-err` : undefined}
            aria-invalid={!!fieldErrors.fullName}
            maxLength={100}
          />
          {fieldErrors.fullName && (
            <p id={`${formId}-name-err`} className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.fullName}</p>
          )}
        </div>
        <div>
          <label htmlFor={`${formId}-email`} className="form-label-base">
            Email <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id={`${formId}-email`}
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="maria@example.com"
            className={`form-input-base${fieldErrors.email ? ' border-red-400 focus:ring-red-300' : ''}`}
            aria-required="true"
            aria-describedby={fieldErrors.email ? `${formId}-email-err` : undefined}
            aria-invalid={!!fieldErrors.email}
            maxLength={254}
          />
          {fieldErrors.email && (
            <p id={`${formId}-email-err`} className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.email}</p>
          )}
        </div>
      </div>

      {/* Phone */}
      <div>
        <label htmlFor={`${formId}-phone`} className="form-label-base">
          Phone <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id={`${formId}-phone`}
          name="phone"
          type="tel"
          required
          autoComplete="tel"
          placeholder="(201) 555-0100"
          className={`form-input-base${fieldErrors.phone ? ' border-red-400 focus:ring-red-300' : ''}`}
          aria-required="true"
          aria-describedby={fieldErrors.phone ? `${formId}-phone-err` : undefined}
          aria-invalid={!!fieldErrors.phone}
          maxLength={30}
        />
        {fieldErrors.phone && (
          <p id={`${formId}-phone-err`} className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.phone}</p>
        )}
      </div>

      {/* Position */}
      <div>
        <label htmlFor={`${formId}-position`} className="form-label-base">
          Position Applying For <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <select
          id={`${formId}-position`}
          name="position"
          required
          className={`form-input-base${fieldErrors.position ? ' border-red-400 focus:ring-red-300' : ''}`}
          aria-required="true"
          aria-describedby={fieldErrors.position ? `${formId}-position-err` : undefined}
          aria-invalid={!!fieldErrors.position}
          defaultValue=""
        >
          <option value="" disabled>Select a position…</option>
          <option value="EMT">EMT</option>
          <option value="Medical Transport Driver">Medical Transport Driver</option>
          <option value="Other">Other</option>
        </select>
        {fieldErrors.position && (
          <p id={`${formId}-position-err`} className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.position}</p>
        )}
      </div>

      {/* EMT Certified */}
      <fieldset>
        <legend className="form-label-base mb-2">
          Are you NJ EMT-certified? <span className="text-red-500" aria-hidden="true">*</span>
        </legend>
        <div className="flex flex-wrap gap-5">
          {['Yes', 'No', 'In Progress'].map((opt) => (
            <label key={opt} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="emtCertified"
                value={opt}
                required
                className="accent-primary w-4 h-4"
              />
              <span className="text-sm text-foreground">{opt}</span>
            </label>
          ))}
        </div>
        {fieldErrors.emtCertified && (
          <p className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.emtCertified}</p>
        )}
      </fieldset>

      {/* Years of experience */}
      <div>
        <label htmlFor={`${formId}-experience`} className="form-label-base">
          Years of Relevant Experience
        </label>
        <input
          id={`${formId}-experience`}
          name="experience"
          type="text"
          placeholder="e.g. 3 years as EMT-B"
          className="form-input-base"
          maxLength={200}
        />
      </div>

      {/* Resume upload */}
      <div>
        <label htmlFor={`${formId}-resume`} className="form-label-base">
          Resume <span className="text-muted-foreground text-xs font-normal">(optional)</span>
        </label>
        <input
          id={`${formId}-resume`}
          name="resume"
          type="file"
          accept=".pdf,.doc,.docx"
          className={`block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer${fieldErrors.resume ? ' border border-red-400 rounded-lg' : ''}`}
          aria-describedby={`${formId}-resume-hint${fieldErrors.resume ? ` ${formId}-resume-err` : ''}`}
          aria-invalid={!!fieldErrors.resume}
        />
        <p id={`${formId}-resume-hint`} className="text-xs text-muted-foreground mt-1">
          PDF, DOC, or DOCX — max 5 MB
        </p>
        {fieldErrors.resume && (
          <p id={`${formId}-resume-err`} className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.resume}</p>
        )}
      </div>

      {/* Cover note */}
      <div>
        <label htmlFor={`${formId}-cover`} className="form-label-base">
          Cover Note <span className="text-muted-foreground font-normal">(optional)</span>
        </label>
        <textarea
          id={`${formId}-cover`}
          name="coverNote"
          rows={4}
          placeholder="Tell us a bit about yourself and why you want to join Kapra…"
          className="form-input-base resize-none"
          maxLength={3000}
        />
      </div>

      {/* Consent */}
      <div className="flex items-start gap-3">
        <input
          id={`${formId}-consent`}
          name="consent"
          type="checkbox"
          required
          className="accent-primary w-4 h-4 mt-0.5 shrink-0"
          aria-required="true"
          aria-describedby={fieldErrors.consent ? `${formId}-consent-err` : undefined}
          aria-invalid={!!fieldErrors.consent}
        />
        <div>
          <label htmlFor={`${formId}-consent`} className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
            I agree to be contacted by Kapra Medical Transportation about this application.{' '}
            <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          {fieldErrors.consent && (
            <p id={`${formId}-consent-err`} className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.consent}</p>
          )}
        </div>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isPending}
        className="btn-primary w-full justify-center py-4 text-base disabled:opacity-60 disabled:cursor-not-allowed"
        aria-label="Submit job application"
      >
        {isPending ? (
          <>
            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Submitting…
          </>
        ) : (
          <>
            <Icon name="PaperAirplaneIcon" size={18} />
            Apply Now
          </>
        )}
      </button>
    </form>
  );
}
